import { useState } from "react";
import { Calendar, Apple, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

export default function CalendarSubscribe({ testId = "calendar-subscribe" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const httpsUrl = `${backend}/api/tournaments.ics`;
  // webcal:// subscribes Apple Calendar / default calendar apps
  const webcalUrl = httpsUrl.replace(/^https?:\/\//, "webcal://");
  const googleUrl = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(webcalUrl)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true);
      toast.success("Subscription URL copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  }

  return (
    <div className="relative inline-block" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-axe-purple px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold text-white hover:bg-axe-purple-hover drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
        data-testid={`${testId}-trigger`}
      >
        <Calendar size={14} /> Subscribe
      </button>

      {open && (
        <>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="fixed inset-0 z-40 bg-transparent cursor-default"
          />
          <div
            className="absolute right-0 mt-3 w-72 rounded-2xl border border-axe-border bg-axe-surface p-3 z-50 shadow-2xl"
            data-testid={`${testId}-menu`}
          >
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.28em] font-bold text-axe-tangerine">
              Add to your calendar
            </div>

            <a
              href={googleUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-google`}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-axe-bg text-sm font-semibold"
            >
              <span className="h-8 w-8 rounded-lg bg-axe-bg border border-axe-border grid place-items-center text-axe-purple font-black">
                G
              </span>
              <span className="flex-1">Google Calendar</span>
            </a>

            <a
              href={webcalUrl}
              onClick={() => setOpen(false)}
              data-testid={`${testId}-apple`}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-axe-bg text-sm font-semibold"
            >
              <span className="h-8 w-8 rounded-lg bg-axe-bg border border-axe-border grid place-items-center">
                <Apple size={14} className="text-white" />
              </span>
              <span className="flex-1">Apple Calendar (.ics)</span>
            </a>

            <a
              href={httpsUrl}
              download
              onClick={() => setOpen(false)}
              data-testid={`${testId}-download`}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-axe-bg text-sm font-semibold"
            >
              <span className="h-8 w-8 rounded-lg bg-axe-bg border border-axe-border grid place-items-center text-axe-tangerine font-black">
                ↓
              </span>
              <span className="flex-1">Download .ics file</span>
            </a>

            <button
              type="button"
              onClick={() => {
                copy();
                setOpen(false);
              }}
              data-testid={`${testId}-copy`}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-axe-bg text-sm font-semibold"
            >
              <span className="h-8 w-8 rounded-lg bg-axe-bg border border-axe-border grid place-items-center">
                {copied ? <Check size={14} className="text-axe-tangerine" /> : <LinkIcon size={14} />}
              </span>
              <span className="flex-1">{copied ? "Copied!" : "Copy subscription URL"}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
